/**
 * @fileoverview Jobs bulk action bar (MOR-2344)
 *
 * Floating bar pinned to the bottom of the viewport while jobs are selected
 * in the Jobs table. Mirrors the Precedent Library's selection bar: the
 * selected count, an "Update status" menu that applies one status to every
 * selected job, and "Done" to clear the selection.
 *
 * Used by: -ui.jobs-data-table.tsx
 */

import { ChevronDown, Loader2, Pencil } from "lucide-react";

import type { JobStatus } from "@cvx/jobs/schema";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  JOB_STATUS_OPTIONS,
  JOB_STATUS_VARIANTS as STATUS_VARIANTS,
} from "@/lib/job-statuses";

interface JobsBulkActionBarProps {
  selectedCount: number;
  /** True while a bulk update is in flight — disables the actions. */
  pending: boolean;
  onUpdateStatus: (status: JobStatus) => void;
  onDone: () => void;
}

export function JobsBulkActionBar({
  selectedCount,
  pending,
  onUpdateStatus,
  onDone,
}: JobsBulkActionBarProps) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
      <div
        role="toolbar"
        aria-label="Bulk job actions"
        className="pointer-events-auto flex items-center gap-3 rounded-full border border-primary-foreground/20 bg-primary px-4 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.12)]"
      >
        <span className="text-sm font-medium text-primary-foreground">
          {selectedCount} selected
        </span>
        <div className="h-4 w-px bg-primary-foreground/20" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              className="h-8 gap-1.5 bg-white text-xs text-primary hover:bg-white/90"
              disabled={pending}
            >
              {pending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Pencil className="size-3.5" />
              )}
              Update status
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="center" className="w-52">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Set status for {selectedCount} job{selectedCount === 1 ? "" : "s"}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {JOB_STATUS_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onSelect={() => onUpdateStatus(option.value)}
              >
                <Badge variant={STATUS_VARIANTS[option.value] ?? "outline"}>
                  {option.label}
                </Badge>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 text-xs text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
          onClick={onDone}
          disabled={pending}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
